/**
 * Type guard to check if the provided location has valid numeric latitude and longitude values within valid ranges.
 *
 * @param {Maybe<{ latitude?: Maybe<number>; longitude?: Maybe<number> }>} location - The location object to validate.
 * @returns {location is { latitude: number; longitude: number }} True if latitude and longitude are both numbers and within valid ranges (latitude: -90 to 90, longitude: -180 to 180).
 */
export const isValidCoordinates = (location?: Maybe<{ latitude?: Maybe<number>; longitude?: Maybe<number> }>): location is { latitude: number; longitude: number } =>
  !!location &&
  typeof location.latitude === 'number' &&
  typeof location.longitude === 'number' &&
  location.latitude >= -90 &&
  location.latitude <= 90 &&
  location.longitude >= -180 &&
  location.longitude <= 180;

/**
 * Converts a location object with optional latitude and longitude into a valid coordinate pair,
 * or returns null if the coordinates are invalid.
 *
 * @param {Maybe<{ latitude?: Maybe<number>; longitude?: Maybe<number> }>} loc - The input location object.
 * @returns {{ lat: number; lng: number } | null} A valid coordinate object or null if invalid.
 */
export const getValidCoordinates = (loc?: Maybe<{ latitude?: Maybe<number>; longitude?: Maybe<number> }>): { lat: number; lng: number } | null =>
  isValidCoordinates(loc) ? { lat: loc.latitude, lng: loc.longitude } : null;
